import { useState } from 'react';
import { Plus, MapPin, Phone, FileText, Pencil, Trash2, User } from 'lucide-react';
import { Button, Modal, Field, inputClass } from '@/components/ui';
import { useMutation } from '@/hooks/useApi';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import type { StoreLocation, StoreAssociate } from './storeTypes';

interface LocationForm {
  name: string;
  address: string;
  phone: string;
  notes: string;
}

const EMPTY: LocationForm = { name: '', address: '', phone: '', notes: '' };

export function StoreLocations({
  storeId,
  locations,
  associates,
  onChanged,
}: {
  storeId: string;
  locations: StoreLocation[];
  associates: StoreAssociate[];
  onChanged: () => void;
}) {
  const { mutate, loading: saving } = useMutation();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<StoreLocation | null>(null);
  const [form, setForm] = useState<LocationForm>(EMPTY);

  const activeAssociates = associates.filter((a) => !a.isArchived);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (loc: StoreLocation) => {
    setEditing(loc);
    setForm({
      name: loc.name ?? '',
      address: loc.address ?? '',
      phone: loc.phone ?? '',
      notes: loc.notes ?? '',
    });
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setEditing(null);
  };

  const handleSave = async () => {
    const body = {
      name: form.name.trim() || undefined,
      address: form.address.trim() || undefined,
      phone: form.phone.trim() || undefined,
      notes: form.notes.trim() || undefined,
    };
    if (!body.name && !body.address) return;
    let ok = false;
    if (editing) {
      try {
        await api.patch(`/store-locations/${editing.id}`, body);
        ok = true;
      } catch {
        ok = false;
      }
    } else {
      const res = await mutate('post', `/stores/${storeId}/locations`, body);
      ok = res !== null;
    }
    if (ok) {
      close();
      onChanged();
    }
  };

  const handleDelete = async (id: string) => {
    const res = await mutate('delete', `/store-locations/${id}`);
    if (res !== null) onChanged();
  };

  return (
    <div className="border-t border-border p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Locations</h4>
        <button
          onClick={openAdd}
          className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Location
        </button>
      </div>

      {locations.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-2">No locations added</p>
      ) : (
        <div className="space-y-2">
          {locations.map((loc) => {
            const here = activeAssociates.filter((a) => a.locationId === loc.id);
            return (
              <div key={loc.id} className="bg-secondary/50 rounded-xl p-3">
                <div className="flex items-start gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {loc.name?.trim() || loc.address?.trim() || 'Location'}
                    </p>
                    {loc.name && loc.address && (
                      <p className="text-xs text-muted-foreground">{loc.address}</p>
                    )}
                  </div>
                  <button
                    onClick={() => openEdit(loc)}
                    className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground"
                    aria-label="Edit location"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(loc.id)}
                    className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive"
                    aria-label="Delete location"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="mt-2 space-y-1 ml-10">
                  {loc.phone && (
                    <a
                      href={`tel:${loc.phone}`}
                      className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary"
                    >
                      <Phone className="w-3 h-3" />
                      {loc.phone}
                    </a>
                  )}
                  {loc.notes && (
                    <div className="flex items-start gap-2 text-xs text-muted-foreground">
                      <FileText className="w-3 h-3 mt-0.5" />
                      <span>{loc.notes}</span>
                    </div>
                  )}
                  {here.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {here.map((sa) => (
                        <span
                          key={sa.id}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-secondary text-secondary-foreground"
                        >
                          <User className="w-3 h-3" />
                          {sa.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={open}
        onClose={close}
        title={editing ? 'Edit Location' : 'Add Location'}
        footer={
          <Button
            fullWidth
            onClick={handleSave}
            disabled={(!form.name.trim() && !form.address.trim()) || saving}
          >
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Location'}
          </Button>
        }
      >
        <div className="space-y-4">
          <Field label="Name">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Madison Avenue"
              className={inputClass}
            />
          </Field>
          <Field label="Address">
            <input
              type="text"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="e.g., 123 Madison Ave, New York, NY"
              className={inputClass}
            />
          </Field>
          <Field label="Phone">
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="e.g., +1 212-555-0123"
              className={inputClass}
            />
          </Field>
          <Field label="Notes">
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Any notes about this location…"
              rows={3}
              className={cn(inputClass, 'resize-none')}
            />
          </Field>
        </div>
      </Modal>
    </div>
  );
}
